import { getSession, signIn } from 'next-auth/react'
import { getCachedAuth, hasFreshCachedToken, setCachedAuth } from '@/lib/auth-token'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

/**
 * A robust API client that handles authentication and base path resolution.
 * @param endpoint The API endpoint to call, e.g., '/api/ai/policies' or '/api/admin/dashboard'.
 *                 The endpoint should include the '/api' prefix.
 * @param options Standard fetch options (method, body, etc.).
 */
type ApiRequestOptions = RequestInit & {
  /** Override the default abort timeout. Only raise it for genuinely slow
   *  transfers (large file uploads) — server-side work is backgrounded. */
  timeoutMs?: number
}

async function resolveAccessToken(): Promise<{
  accessToken?: string
  error?: string
}> {
  // Prefer the in-memory token synced from useSession — avoids a
  // NextAuth /api/auth/session round-trip on every API call.
  if (hasFreshCachedToken()) {
    const cached = getCachedAuth()
    return { accessToken: cached.accessToken || undefined, error: cached.error }
  }

  const session = await getSession()
  if (session?.accessToken) {
    setCachedAuth({
      accessToken: session.accessToken,
      error: session.error,
      expiresAt: session.accessTokenExpires,
    })
  }
  return {
    accessToken: session?.accessToken,
    error: session?.error,
  }
}

async function apiClientFetch<T = unknown>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const { accessToken, error } = await resolveAccessToken()
  const { timeoutMs: timeoutOverride, ...fetchOptions } = options

  const headers = new Headers(fetchOptions.headers || {})

  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }

  // Handle session errors that occurred during a token refresh attempt.
  if (error === 'RefreshAccessTokenError') {
    // Force sign-in to get a new, valid session.
    await signIn('keycloak')
    // Throw an error to stop the current, failing API call.
    throw new Error('Session expired. Please sign in again.')
  }

  // Construct the full URL including optional BASE_PATH
  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`

  // Add timeout to prevent hanging requests (30 seconds default)
  const DEFAULT_TIMEOUT_MS = 30000
  const timeoutMs = timeoutOverride ?? DEFAULT_TIMEOUT_MS

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const response = await fetch(fullUrl, { 
      ...fetchOptions, 
      headers,
      signal: controller.signal 
    })

    clearTimeout(timeoutId)

    // If the backend returns a 401, it means the token is invalid or expired.
    // This can happen if the backend is restarted or the user's session is revoked.
    // We trigger a re-login to fix the session.
    if (response.status === 401) {
      await signIn('keycloak')
      throw new Error('Your session has expired. Please sign in again.')
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        detail: response.statusText,
      }))
      const detail = errorData.detail
      const message =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail
                .map((item) =>
                  typeof item === 'string'
                    ? item
                    : item?.msg || JSON.stringify(item)
                )
                .join('; ')
            : detail
              ? JSON.stringify(detail)
              : 'An API error occurred.'
      throw new Error(message)
    }

    // Handle responses with no content
    if (response.status === 204) {
      return null as T
    }

    return response.json() as Promise<T>
  } catch (error) {
    clearTimeout(timeoutId)
    
    // Handle abort/timeout errors gracefully
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. The server may be processing a long operation.')
    }
    
    throw error
  }
}

/**
 * API client with convenience methods for common HTTP operations.
 */
export const apiClient = {
  /**
   * Perform a GET request.
   */
  get: <T = unknown>(endpoint: string, options?: ApiRequestOptions): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'GET' })
  },

  /**
   * Perform a POST request.
   */
  post: <T = unknown>(endpoint: string, data?: unknown, options?: ApiRequestOptions): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PUT request.
   */
  put: <T = unknown>(endpoint: string, data?: unknown, options?: ApiRequestOptions): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PATCH request.
   */
  patch: <T = unknown>(endpoint: string, data?: unknown, options?: ApiRequestOptions): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a DELETE request.
   */
  delete: <T = unknown>(endpoint: string, options?: ApiRequestOptions): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'DELETE' })
  },

  /**
   * Perform a multipart/form-data POST (file uploads). Never set
   * Content-Type manually here — the browser must set the multipart
   * boundary itself.
   *
   * The timeout is generous because it has to cover pushing the bytes over the
   * wire, which a 40-vendor ZIP pair on a slow link can easily take longer than
   * 30s to do. The server no longer processes uploads inline, so this only ever
   * bounds the transfer itself.
   */
  upload: <T = unknown>(
    endpoint: string,
    formData: FormData,
    options?: ApiRequestOptions
  ): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      timeoutMs: 600000,
      ...options,
      method: 'POST',
      body: formData,
    })
  },
}

// Default export for backward compatibility
export default apiClientFetch
